import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import { Button } from 'components/UI';
import { iconSuccess, iconLinkCheckmark, iconPromo } from 'assets';
import { ALL_ACCESS, PREMIUM, FREE, membershipBenefits } from 'utils/constants';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';
import { Input, OutlinedInput, TextField } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import Selectors from 'selectors';
import Actions from 'actions';
import { Spinner } from 'components/UI/Loading';
import { StyledImg } from 'components/Form/SocialMediaLinkInput';
import classnames from 'classnames';
import PromoCode from './PromoCode';
import { debounce } from 'lodash';

const renderYearlyPlan = (coupon, yearlyPriceByMonth, yearlyPrice, savings) => {
  if (coupon) return (
    <>
      <div className="flex items-baseline">
        <span className="text-2lg font-bold text-gray-800">
          ${yearlyPrice}
        </span>
        <span className="text-sm text-gray-500 ml-1">USD/year</span>
      </div>
      <p className="text-xs text-gray-500 italic mt-1">
        Billed annually
      </p>
    </>
  )
  return (
    <>
      <div className="flex items-baseline">
        <span className="text-2lg font-bold text-gray-800">
          ${yearlyPriceByMonth}
        </span>
        <span className="text-sm text-gray-500 ml-1">USD/month</span>
      </div>
      <p className="text-xs text-gray-500 italic mt-1">
        Billed annually (USD {yearlyPrice}/year)
      </p>
      {Number(savings) > 0 && (
        <p className="text-xs font-semibold text-easter mt-1">
          Save USD {savings}/year
        </p>
      )}
    </>
  )
}

const TierInfoCard = ({
  memberTier,
  prices,
  isAllAccessEligible,
  showNextScreen,
  selectedPlan,
  setSelectedPlan,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {signupCode} = useQuery();
  const { monthlyPrice, yearlyPrice, savings, yearlyPriceByMonth, selectedPlan: selectedPlanData } = useSubscriptionPlan(selectedPlan, prices);
  const [promoCode, setPromoCode] = useState('');
  const [validPromo, setValidPromo] = useState(null);
  const [errorPromo, setErrorPromo] = useState(null);

  useEffect(() => {
    if (signupCode) {
      setPromoCode(signupCode);
    }
  }, [signupCode]);

  useEffect(() => {
    const handler = debounce((code) => {
      const trimmedCode = code.trim();
      if (!trimmedCode) {
        setErrorPromo(null);
        setValidPromo(null);
        dispatch(Actions.clearPromo());
        return
      };
      dispatch(
        Actions.validatePromoCode(
          { code: trimmedCode, price_id: selectedPlanData.id },
          {
            success: (data) => {
              setValidPromo(data);
              setErrorPromo(null);
            },
            failure: (error) => {
              setValidPromo(null);
              if (error?.response?.status === 404) {
                setErrorPromo('Invalid promo code');
              } else setErrorPromo(error?.response?.data?.error || error?.message);
            },
          }
        )
      );
    }, 500);

    if(selectedPlanData) handler(promoCode);

    return () => handler.cancel();
  }, [promoCode, dispatch, selectedPlan, setErrorPromo, selectedPlanData]);

  const showRecommended = isAllAccessEligible
    ? memberTier === ALL_ACCESS
    : memberTier === PREMIUM;

  const isFree = memberTier === FREE;

  const contentTexts = membershipBenefits[memberTier] || [];

  const handleSignUp = () => {
    // if (memberTier && selectedPlan) {
    //   showNextScreen();
    //   return;
    // }
    let search = `?memberTier=${memberTier}`
    if (!isFree) {
      search += `&plan=${selectedPlan}`;
      if(validPromo?.id && promoCode) {
        search += `&signupCode=${promoCode}`;
      }
    }
    navigate({
      pathname: '/signup',
      search,
    });
    showNextScreen();
  };

  const renderPricing = () => {
    if (isFree) {
      return (
        // <div className="flex flex-col items-center mt-4">
        //   <div className="bg-gray-100 rounded-lg px-6 py-4 w-full text-center border border-gray-200">
        //     <span className="text-xl font-bold text-gray-800">$0</span>
        //     <span className="text-sm text-gray-500 ml-1">USD</span>
        //     <p className="text-xs text-gray-400 mt-1">Free forever</p>
        //   </div>
        // </div>
        null
      );
    }

    const isAnnualSelected = selectedPlan === 'year';

    return (
      <div className="flex flex-col space-y-3 mt-4">
        {/* Annual Plan */}
        <div
          className="rounded-lg px-4 py-3 cursor-pointer"
          style={{
            background: isAnnualSelected
              ? 'rgba(71, 186, 212, 0.08)'
              : '#f7fafc',
            border: isAnnualSelected
              ? '1.5px solid rgba(71, 186, 212, 0.5)'
              : '1.5px solid #e2e8f0',
            transition: 'all 0.2s ease',
          }}
          onClick={() => setSelectedPlan('year')}
        >
          <div className="flex items-center mb-1">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                color: isAnnualSelected ? '#47bad4' : '#a0aec0',
              }}
            >
              Annual Plan
            </span>
          </div>
          {renderYearlyPlan(validPromo?.id, yearlyPriceByMonth, yearlyPrice, savings)}
        </div>

        {/* Monthly Plan */}
        <div
          className="rounded-lg px-4 py-3 cursor-pointer"
          style={{
            background: !isAnnualSelected
              ? 'rgba(71, 186, 212, 0.08)'
              : '#f7fafc',
            border: !isAnnualSelected
              ? '1.5px solid rgba(71, 186, 212, 0.5)'
              : '1.5px solid #e2e8f0',
            transition: 'all 0.2s ease',
          }}
          onClick={() => setSelectedPlan('month')}
        >
          <div className="flex items-center mb-1">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                color: !isAnnualSelected ? '#47bad4' : '#a0aec0',
              }}
            >
              Monthly Plan
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="text-2lg font-bold text-gray-800">
              ${monthlyPrice ? monthlyPrice.toFixed(2) : '0.00'}
            </span>
            <span className="text-sm text-gray-500 ml-1">USD/month</span>
          </div>
          <p className="text-xs text-gray-500 italic mt-1">
            Billed monthly
          </p>
        </div>
      </div>
    );
  };

  const renderBenefitItem = (item, index) => {
    const text = typeof item === 'object' ? item.text : item;
    const isBold = typeof item === 'object' && item.bold;

    return (
      <div className="flex space-x-2" key={index}>
        <img
          alt="check"
          src={iconSuccess.default}
          className="h-3 lg:h-4 mt-1 flex-shrink-0"
        />
        <li className={isBold ? 'font-semibold text-easter' : ''}>
          {text}
        </li>
      </div>
    );
  };

  const handleChange = (e) => {
    setPromoCode(e.target.value);
    setErrorPromo(null);
  };

  return (
    <>
      <div className="rounded-lg flex flex-col bg-white md:w-5/12">
        <div className="p-6 flex flex-col justify-between flex-1">
          <div>
            {showRecommended && (
              <div className="bg-yellow-500 text-xs rounded-full py-2 text-center w-1/2 md:w-8/12 mx-auto -mt-10">
                Recommended
              </div>
            )}
            <h4 className="font-semibold text-2lg py-5">{memberTier}</h4>

            {/* Content from membershipBenefits */}
            {contentTexts.length > 0 && (
              <ul className="space-y-3 mt-1 font-light text-sm">
                {contentTexts.map((item, index) =>
                  renderBenefitItem(item, index)
                )}
              </ul>
            )}
          </div>

          {/* Pricing Section */}
          <div>{renderPricing()}</div>
          {!isFree && (
            <div className='mt-4'>
              <PromoCode setPromoCode={setPromoCode} handleChange={handleChange} promoCode={promoCode} />
              {errorPromo && <p className='text-red-500 text-xs mt-2'>{errorPromo}</p>}
            </div>
          )}
        </div>
        {!isSignupPaused && <div className="w-full flex items-center h-10">
          <div className="flex-grow border-t border-gray-400" />
          <Button
            label={`Apply for ${memberTier}`}
            className="w-full text-xs lg:text-sm"
            onClick={handleSignUp}
          />
          <div className="flex-grow border-t border-gray-400" />
        </div>}
        <div className="text-sm px-4 py-6">
          <p>Benefits:</p>
          <ul className="space-y-4 mt-3 font-light">
            {benefits &&
              benefits.map((text) => (
                <div className="flex space-x-3" key={text}>
                  <img
                    alt="check"
                    src={iconSuccess.default}
                    className="h-3 lg:h-4 mt-1"
                  />
                  <li>{text}</li>
                </div>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
};

TierInfoCard.propTypes = {
  memberTier: PropTypes.string.isRequired,
  prices: PropTypes.array,
  isAllAccessEligible: PropTypes.bool,
  monthlyPrice: PropTypes.number,
  annualPrice: PropTypes.number,
  showNextScreen: PropTypes.func,
};

TierInfoCard.defaultProps = {
  prices: [],
  monthlyPrice: 0,
  annualPrice: 0,
  isAllAccessEligible: false,
  showNextScreen: () => {},
};

export default TierInfoCard;
